import {
  Header,
  QuestionnaireFormProps,
  QuestionnaireQuestions,
} from '@getpopsure/qnr-framework';
import Session from '@getpopsure/session';
import { setAccountDataInQuestionnaire } from 'actions/account';
import { AccountView } from 'components/AccountEmail/components/Account/Account.view';
import * as EmailComponents from 'components/AccountEmail/components/Email/Email';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppState } from 'reducers';
import { GenericQuestionnaireState } from 'reducers/genericQuestionnaire';
import { getRequestError, getRequestInProcess } from 'selectors/requests';
import { getAccountInfo, getEmail } from 'selectors/user';
import { useEmailQuestion } from 'SignupQuestionnaire/components//Email/useEmailQuestion';
import LoginModal from 'SignupQuestionnaire/components/Email/LoginModal';

import styles from './Email.module.scss';

const { Title, Subtitle, Input, GoogleError, Actions } = EmailComponents;

export const Email = <Questionnaire extends QuestionnaireQuestions>({
  value,
  onSubmitValue,
  verticalId,
  setValidForSubmission,
  questionnaireAnswers,
  question,
  emailParam,
  showTerms = true,
}: {
  verticalId: keyof GenericQuestionnaireState;
  emailParam?: string | null;
  showTerms?: boolean;
} & QuestionnaireFormProps<Questionnaire>) => {
  const { isAuthenticated } = Session;
  const [googleLoginError, setGoogleLoginError] = useState(false);
  const [isLoginWithGoogle, setIsLoginWithGoogle] = useState(false);
  const [continueWithEmail, setContinueWithEmail] = useState(false);
  const [createNewAccount, setCreateNewAccount] = useState(false);
  const dispatch = useDispatch();
  const accountEmail = useSelector(getEmail);
  const account = useSelector(getAccountInfo);
  const redirectPath =
    typeof questionnaireAnswers.redirectPath === 'string'
      ? questionnaireAnswers.redirectPath
      : '';

  const loadingGoogle = useSelector((state: AppState) =>
    getRequestInProcess(state, 'SET_ACCOUNT_DATA_IN_QUESTIONNAIRE')
  );
  const errorGoogle = useSelector((state: AppState) =>
    getRequestError(state, 'SET_ACCOUNT_DATA_IN_QUESTIONNAIRE')
  );

  useEffect(() => {
    if (!errorGoogle && !loadingGoogle && accountEmail && isLoginWithGoogle) {
      setIsLoginWithGoogle(false);
      onSubmitValue(accountEmail);
    }
  }, [
    errorGoogle,
    loadingGoogle,
    accountEmail,
    onSubmitValue,
    isLoginWithGoogle,
  ]);

  const {
    email,
    handleInputChange,
    handleModalClose,
    handleSubmit,
    isValid,
    onLogin,
    requiresLoginCode,
    toggleTermsAccepted,
  } = useEmailQuestion({
    onSubmitValue,
    setValidForSubmission,
    showTerms,
    value,
  });

  const handleOnGoogleLogin = () => {
    dispatch(setAccountDataInQuestionnaire(redirectPath, verticalId));
    setIsLoginWithGoogle(true);
  };

  const isSameEmail = accountEmail === email;
  if (
    account &&
    account.firstName &&
    account.lastName &&
    isAuthenticated &&
    !createNewAccount &&
    isSameEmail
  ) {
    return (
      <AccountView
        email={account.email}
        name={{
          firstName: account.firstName,
          lastName: account.lastName,
        }}
        handleContinueWithCurrentAccount={() => onSubmitValue(account.email)}
        handleCreateNewAccount={() => setCreateNewAccount(true)}
      />
    );
  }

  const isEmailSet = continueWithEmail || emailParam;
  return (
    <div className="p-body">
      <div className={styles.container}>
        <Header onSubmit={handleSubmit}>
          <Title text={question} />
          <Subtitle />
          <Input
            show={Boolean(isEmailSet)}
            value={(value as string) ?? email}
            emailInputOnChange={handleInputChange}
            tAndCInputOnChange={toggleTermsAccepted}
            showTerms={showTerms}
          />
          <div>{googleLoginError && <GoogleError />}</div>
          <Actions
            isEmailSet={isEmailSet}
            disabled={!isValid}
            setContinueWithEmail={setContinueWithEmail}
            googleOnSuccess={handleOnGoogleLogin}
            googleOnError={() => {
              setIsLoginWithGoogle(false);
              setGoogleLoginError(true);
            }}
          />
        </Header>
      </div>
      <LoginModal
        email={email}
        isOpen={requiresLoginCode}
        onClose={handleModalClose}
        onLogin={onLogin}
        verticalId={verticalId}
      />
    </div>
  );
};
